(() => {
  'use strict';

  class StudioPremiumProduct {
    constructor(section) {
      this.section = section;

      this.variants = this.readVariants();

      this.optionInputs = Array.from(
        section.querySelectorAll('[data-option-value]')
      );

      this.variantInput = section.querySelector(
        '[data-variant-id]'
      );

      this.price = section.querySelector(
        '[data-product-price]'
      );

      this.comparePrice = section.querySelector(
        '[data-product-compare-price]'
      );

      this.mobilePrice = section.querySelector(
        '[data-mobile-product-price]'
      );

      this.stock = section.querySelector(
        '[data-product-stock]'
      );

      this.stockText = section.querySelector(
        '[data-stock-text]'
      );

      this.stockBar = section.querySelector(
        '[data-stock-bar]'
      );

      this.addButton = section.querySelector(
        '[data-add-to-cart]'
      );

      this.addText = section.querySelector(
        '[data-add-text]'
      );

      this.mobileAdd = section.querySelector(
        '[data-mobile-add]'
      );

      this.qtyInput = section.querySelector(
        '[data-qty-input]'
      );

      this.qtyMinus = section.querySelector(
        '[data-qty-minus]'
      );

      this.qtyPlus = section.querySelector(
        '[data-qty-plus]'
      );

      this.media = Array.from(
        section.querySelectorAll(
          '[data-product-media]'
        )
      );

      this.thumbs = Array.from(
        section.querySelectorAll(
          '[data-gallery-thumb]'
        )
      );

      this.prev = section.querySelector(
        '[data-gallery-prev]'
      );

      this.next = section.querySelector(
        '[data-gallery-next]'
      );

      this.zoom = section.querySelector(
        '[data-gallery-zoom]'
      );

      this.wishlist = section.querySelector(
        '.studio-pdp-wishlist'
      );

      this.currentMediaIndex = Math.max(
        0,
        this.media.findIndex((item) =>
          item.classList.contains('is-active')
        )
      );

      this.lightbox = null;
      this.lightboxImage = null;
      this.lightboxCounter = null;
      this.lightboxThumbs = [];
      this.lightboxIndex = 0;

      this.init();
    }


    init() {
      this.setupVariants();
      this.updateOptionAvailability();
      this.setupQuantity();
      this.setupGallery();
      this.setupMobileAdd();
      this.setupWishlist();
      this.setupLightbox();
    }


    /* ==========================================================
       VARIANTS
       ========================================================== */

    readVariants() {
      const script =
        this.section.querySelector(
          '[data-product-variants]'
        );

      if (!script) {
        return [];
      }

      try {
        return JSON.parse(
          script.textContent
        );
      } catch (error) {
        console.error(
          'Studio Commerce: product variants JSON error',
          error
        );

        return [];
      }
    }


    setupVariants() {
      this.optionInputs.forEach(
        (input) => {

          input.addEventListener(
            'change',
            () => {

              this.updateSelectedLabels();

              const variant =
                this.findVariant();

              if (!variant) {
                this.setUnavailable();
                this.updateOptionAvailability();
                return;
              }

              this.setVariant(variant);
              this.updateOptionAvailability();
            }
          );

        }
      );
    }


    selectedOptions() {
      const selected = [];

      this.optionInputs
        .filter(
          (input) => input.checked
        )
        .forEach(
          (input) => {

            selected[
              Number(
                input.dataset.optionIndex
              )
            ] = input.value;

          }
        );

      return selected;
    }


    findVariant() {
      const selected =
        this.selectedOptions();

      return this.variants.find(
        (variant) => {

          return variant.options.every(
            (option, index) => {

              return (
                option === selected[index]
              );

            }
          );

        }
      );
    }


    setVariant(variant) {
      if (this.variantInput) {
        this.variantInput.value =
          variant.id;
      }

      this.updatePrice(variant);
      this.updateAvailability(variant);
      this.updateUrl(variant);
      this.updateVariantMedia(variant);
    }


    updateSelectedLabels() {
      this.section
        .querySelectorAll(
          '[data-option-group]'
        )
        .forEach(
          (group) => {

            const selected =
              group.querySelector(
                '[data-option-value]:checked'
              );

            const output =
              group.querySelector(
                '[data-option-selected]'
              );

            if (
              selected &&
              output
            ) {
              output.textContent =
                selected.value;
            }

          }
        );
    }


    updateOptionAvailability() {
      if (
        !this.optionInputs.length ||
        !this.variants.length
      ) {
        return;
      }

      const selected =
        this.selectedOptions();

      this.optionInputs.forEach(
        (input) => {

          const optionIndex =
            Number(
              input.dataset.optionIndex
            );

          const candidate =
            [...selected];

          candidate[optionIndex] =
            input.value;

          const possible =
            this.variants.some(
              (variant) => {

                if (!variant.available) {
                  return false;
                }

                return variant.options.every(
                  (option, index) => {

                    if (
                      index === optionIndex
                    ) {
                      return (
                        option === input.value
                      );
                    }

                    if (
                      !candidate[index]
                    ) {
                      return true;
                    }

                    return (
                      option ===
                      candidate[index]
                    );

                  }
                );

              }
            );

          input.disabled =
            !possible;

        }
      );
    }


    /* ==========================================================
       PRICE
       ========================================================== */

    updatePrice(variant) {
      if (!this.price) {
        return;
      }

      this.price.textContent =
        this.money(
          variant.price
        );

      const isSale =
        variant.compare_at_price &&
        variant.compare_at_price >
          variant.price;

      this.price.classList.toggle(
        'is-sale',
        Boolean(isSale)
      );


      if (this.comparePrice) {

        if (isSale) {

          this.comparePrice.textContent =
            this.money(
              variant.compare_at_price
            );

          this.comparePrice.classList.remove(
            'is-hidden'
          );

        } else {

          this.comparePrice.textContent =
            '';

          this.comparePrice.classList.add(
            'is-hidden'
          );

        }

      }


      if (this.mobilePrice) {

        this.mobilePrice.textContent =
          this.money(
            variant.price
          );

      }
    }


    /* ==========================================================
       AVAILABILITY / STOCK
       ========================================================== */

    updateAvailability(variant) {
      const available =
        Boolean(
          variant.available
        );


      /* ADD TO CART */

      if (this.addButton) {
        this.addButton.disabled =
          !available;
      }

      if (this.addText) {

        this.addText.textContent =
          available
            ? 'In winkelwagen'
            : 'Uitverkocht';

      }


      /* MOBILE */

      if (this.mobileAdd) {

        this.mobileAdd.disabled =
          !available;

        this.mobileAdd.textContent =
          available
            ? 'In winkelwagen'
            : 'Uitverkocht';

      }


      if (!this.stock) {
        return;
      }


      const tracked =
        Boolean(
          variant.inventory_management
        );

      const quantity =
        Number(
          variant.inventory_quantity ||
          0
        );


      this.stock.classList.remove(
        'is-out',
        'is-low'
      );


      /* UITVERKOCHT */

      if (!available) {

        this.stock.classList.add(
          'is-out'
        );

        if (this.stockText) {
          this.stockText.textContent =
            'Tijdelijk uitverkocht';
        }

        if (this.stockBar) {
          this.stockBar.style.width =
            '0%';
        }

        return;
      }


      /* VOORRAAD NIET GEVOLGD */

      if (!tracked) {

        if (this.stockText) {
          this.stockText.textContent =
            'Beschikbaar';
        }

        if (this.stockBar) {
          this.stockBar.style.width =
            '85%';
        }

        return;
      }


      /* NUL VOORRAAD */

      if (quantity <= 0) {

        const continueSelling =
          variant.inventory_policy ===
          'continue';

        if (this.stockText) {

          this.stockText.textContent =
            continueSelling
              ? 'Beschikbaar op bestelling'
              : 'Beschikbaar';

        }

        if (this.stockBar) {

          this.stockBar.style.width =
            continueSelling
              ? '20%'
              : '85%';

        }

        return;
      }


      /* ZEER LAGE VOORRAAD */

      if (quantity <= 3) {

        this.stock.classList.add(
          'is-low'
        );

        if (this.stockText) {

          this.stockText.textContent =
            `Nog ${quantity} op voorraad — bestel snel!`;

        }

        if (this.stockBar) {

          this.stockBar.style.width =
            '25%';

        }

        return;
      }


      /* LAGE VOORRAAD */

      if (quantity <= 6) {

        this.stock.classList.add(
          'is-low'
        );

        if (this.stockText) {

          this.stockText.textContent =
            `Nog ${quantity} op voorraad`;

        }

        if (this.stockBar) {

          this.stockBar.style.width =
            '45%';

        }

        return;
      }


      /* NORMALE VOORRAAD */

      if (this.stockText) {

        this.stockText.textContent =
          'Beschikbaar';

      }

      if (this.stockBar) {

        this.stockBar.style.width =
          '85%';

      }
    }


    setUnavailable() {
      if (this.addButton) {
        this.addButton.disabled =
          true;
      }

      if (this.addText) {
        this.addText.textContent =
          'Niet beschikbaar';
      }

      if (this.mobileAdd) {

        this.mobileAdd.disabled =
          true;

        this.mobileAdd.textContent =
          'Niet beschikbaar';

      }

      if (this.stock) {
        this.stock.classList.add(
          'is-out'
        );
      }

      if (this.stockText) {
        this.stockText.textContent =
          'Niet beschikbaar';
      }

      if (this.stockBar) {
        this.stockBar.style.width =
          '0%';
      }
    }


    updateUrl(variant) {
      if (
        !variant ||
        !window.history.replaceState
      ) {
        return;
      }

      const url =
        new URL(
          window.location.href
        );

      url.searchParams.set(
        'variant',
        variant.id
      );

      window.history.replaceState(
        {},
        '',
        url.toString()
      );
    }


    updateVariantMedia(variant) {
      if (
        !variant.featured_media
      ) {
        return;
      }

      const id =
        String(
          variant.featured_media.id
        );

      const index =
        this.media.findIndex(
          (item) => {

            return (
              String(
                item.dataset.productMedia
              ) === id
            );

          }
        );

      if (index >= 0) {
        this.showMedia(index);
      }
    }


    /* ==========================================================
       QUANTITY
       ========================================================== */

    setupQuantity() {
      if (!this.qtyInput) {
        return;
      }

      if (this.qtyMinus) {

        this.qtyMinus.addEventListener(
          'click',
          () => {

            const current =
              Number(
                this.qtyInput.value
              ) || 1;

            this.qtyInput.value =
              Math.max(
                1,
                current - 1
              );

            this.qtyInput.dispatchEvent(
              new Event(
                'change',
                {
                  bubbles: true
                }
              )
            );

          }
        );

      }


      if (this.qtyPlus) {

        this.qtyPlus.addEventListener(
          'click',
          () => {

            const current =
              Number(
                this.qtyInput.value
              ) || 1;

            this.qtyInput.value =
              current + 1;

            this.qtyInput.dispatchEvent(
              new Event(
                'change',
                {
                  bubbles: true
                }
              )
            );

          }
        );

      }


      this.qtyInput.addEventListener(
        'change',
        () => {

          let value =
            Number(
              this.qtyInput.value
            ) || 1;

          if (value < 1) {
            value = 1;
          }

          this.qtyInput.value =
            Math.floor(value);

        }
      );
    }
        /* ==========================================================
       GALLERY
       ========================================================== */

    setupGallery() {
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener(
          'click',
          () => {
            const id =
              String(
                thumb.dataset.galleryThumb
              );

            const index =
              this.media.findIndex(
                (item) => {
                  return (
                    String(
                      item.dataset.productMedia
                    ) === id
                  );
                }
              );

            if (index >= 0) {
              this.showMedia(index);
            }
          }
        );
      });


      if (this.prev) {
        this.prev.addEventListener(
          'click',
          (event) => {
            event.stopPropagation();

            if (!this.media.length) {
              return;
            }

            const next =
              (
                this.currentMediaIndex -
                1 +
                this.media.length
              ) %
              this.media.length;

            this.showMedia(next);
          }
        );
      }


      if (this.next) {
        this.next.addEventListener(
          'click',
          (event) => {
            event.stopPropagation();

            if (!this.media.length) {
              return;
            }

            const next =
              (
                this.currentMediaIndex +
                1
              ) %
              this.media.length;

            this.showMedia(next);
          }
        );
      }


      this.setupGallerySwipe();
    }


    showMedia(index) {
      if (!this.media[index]) {
        return;
      }

      this.currentMediaIndex = index;

      this.media.forEach(
        (item, itemIndex) => {
          item.classList.toggle(
            'is-active',
            itemIndex === index
          );
        }
      );


      const activeId =
        String(
          this.media[index]
            .dataset.productMedia
        );


      let activeThumb = null;


      this.thumbs.forEach(
        (thumb) => {
          const active =
            String(
              thumb.dataset.galleryThumb
            ) === activeId;

          thumb.classList.toggle(
            'is-active',
            active
          );

          if (active) {
            activeThumb = thumb;
          }
        }
      );


      if (activeThumb) {
        activeThumb.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }


    setupGallerySwipe() {
      const gallery =
        this.section.querySelector(
          '.studio-pdp-gallery__main'
        );

      if (
        !gallery ||
        this.media.length < 2
      ) {
        return;
      }


      let startX = 0;
      let endX = 0;


      gallery.addEventListener(
        'touchstart',
        (event) => {
          startX =
            event.touches[0]
              .clientX;

          endX = startX;
        },
        {
          passive: true
        }
      );


      gallery.addEventListener(
        'touchmove',
        (event) => {
          endX =
            event.touches[0]
              .clientX;
        },
        {
          passive: true
        }
      );


      gallery.addEventListener(
        'touchend',
        () => {
          const distance =
            endX - startX;

          if (
            Math.abs(distance) < 45
          ) {
            return;
          }

          if (distance < 0) {
            const next =
              (
                this.currentMediaIndex +
                1
              ) %
              this.media.length;

            this.showMedia(next);
          } else {
            const previous =
              (
                this.currentMediaIndex -
                1 +
                this.media.length
              ) %
              this.media.length;

            this.showMedia(previous);
          }
        }
      );
    }


    /* ==========================================================
       LIGHTBOX
       ========================================================== */

    setupLightbox() {
      if (!this.media.length) {
        return;
      }

      this.createLightbox();


      this.media.forEach(
        (mediaItem, index) => {
          const image =
            mediaItem.querySelector(
              'img'
            );

          if (!image) {
            return;
          }

          mediaItem.addEventListener(
            'click',
            (event) => {
              if (
                event.target.closest(
                  'button'
                )
              ) {
                return;
              }

              this.openLightbox(
                index
              );
            }
          );
        }
      );


      if (this.zoom) {
        this.zoom.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();

            this.openLightbox(
              this.currentMediaIndex
            );
          }
        );
      }
    }


    createLightbox() {
      const wrapper =
        document.createElement(
          'div'
        );

      wrapper.className =
        'studio-product-lightbox';

      wrapper.setAttribute(
        'aria-hidden',
        'true'
      );


      wrapper.innerHTML = `
        <div class="studio-product-lightbox__inner">

          <button
            type="button"
            class="studio-product-lightbox__close"
            aria-label="Sluiten"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M6 6l12 12M18 6 6 18"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              />
            </svg>
          </button>

          <button
            type="button"
            class="
              studio-product-lightbox__arrow
              studio-product-lightbox__arrow--prev
            "
            aria-label="Vorige afbeelding"
          >
            ‹
          </button>

          <div class="studio-product-lightbox__image-wrap">
            <img
              class="studio-product-lightbox__image"
              alt=""
            >
          </div>

          <button
            type="button"
            class="
              studio-product-lightbox__arrow
              studio-product-lightbox__arrow--next
            "
            aria-label="Volgende afbeelding"
          >
            ›
          </button>

          <div class="studio-product-lightbox__thumbs"></div>

          <div class="studio-product-lightbox__counter">
            1 / 1
          </div>

        </div>
      `;


      document.body.appendChild(
        wrapper
      );


      this.lightbox =
        wrapper;


      this.lightboxImage =
        wrapper.querySelector(
          '.studio-product-lightbox__image'
        );


      this.lightboxCounter =
        wrapper.querySelector(
          '.studio-product-lightbox__counter'
        );


      const close =
        wrapper.querySelector(
          '.studio-product-lightbox__close'
        );


      const prev =
        wrapper.querySelector(
          '.studio-product-lightbox__arrow--prev'
        );


      const next =
        wrapper.querySelector(
          '.studio-product-lightbox__arrow--next'
        );


      const thumbsContainer =
        wrapper.querySelector(
          '.studio-product-lightbox__thumbs'
        );


      this.createLightboxThumbs(
        thumbsContainer
      );


      close?.addEventListener(
        'click',
        () => {
          this.closeLightbox();
        }
      );


      prev?.addEventListener(
        'click',
        () => {
          this.showPreviousLightbox();
        }
      );


      next?.addEventListener(
        'click',
        () => {
          this.showNextLightbox();
        }
      );


      wrapper.addEventListener(
        'click',
        (event) => {
          if (
            event.target === wrapper ||
            event.target.classList.contains(
              'studio-product-lightbox__inner'
            )
          ) {
            this.closeLightbox();
          }
        }
      );


      document.addEventListener(
        'keydown',
        (event) => {
          if (
            !this.lightbox ||
            !this.lightbox
              .classList
              .contains(
                'is-open'
              )
          ) {
            return;
          }

          if (
            event.key ===
            'Escape'
          ) {
            this.closeLightbox();
          }

          if (
            event.key ===
            'ArrowLeft'
          ) {
            this.showPreviousLightbox();
          }

          if (
            event.key ===
            'ArrowRight'
          ) {
            this.showNextLightbox();
          }
        }
      );


      this.setupLightboxSwipe();
    }


    createLightboxThumbs(
      container
    ) {
      if (!container) {
        return;
      }

      this.lightboxThumbs = [];


      this.media.forEach(
        (mediaItem, index) => {
          const image =
            mediaItem.querySelector(
              'img'
            );

          if (!image) {
            return;
          }


          const button =
            document.createElement(
              'button'
            );


          button.type =
            'button';


          button.className =
            'studio-product-lightbox__thumb';


          const thumb =
            document.createElement(
              'img'
            );


          thumb.src =
            image.currentSrc ||
            image.src;


          thumb.alt =
            image.alt || '';


          button.appendChild(
            thumb
          );


          button.addEventListener(
            'click',
            () => {
              this.showLightboxMedia(
                index
              );
            }
          );


          container.appendChild(
            button
          );


          this.lightboxThumbs[
            index
          ] = button;
        }
      );
    }


    openLightbox(index) {
      if (
        !this.lightbox ||
        !this.media[index]
      ) {
        return;
      }

      this.lightboxIndex =
        index;


      this.showLightboxMedia(
        index
      );


      this.lightbox.classList.add(
        'is-open'
      );


      this.lightbox.setAttribute(
        'aria-hidden',
        'false'
      );


      document.body.classList.add(
        'studio-lightbox-open'
      );


      requestAnimationFrame(
        () => {
          this.lightbox.classList.add(
            'is-visible'
          );
        }
      );
    }


    closeLightbox() {
      if (!this.lightbox) {
        return;
      }


      this.lightbox.classList.remove(
        'is-visible'
      );


      document.body.classList.remove(
        'studio-lightbox-open'
      );


      window.setTimeout(
        () => {
          this.lightbox.classList.remove(
            'is-open'
          );

          this.lightbox.setAttribute(
            'aria-hidden',
            'true'
          );
        },
        220
      );
    }


    showLightboxMedia(index) {
      if (
        !this.media[index] ||
        !this.lightboxImage
      ) {
        return;
      }


      const sourceImage =
        this.media[index]
          .querySelector(
            'img'
          );


      if (!sourceImage) {
        return;
      }


      this.lightboxIndex =
        index;


      this.lightboxImage
        .classList
        .add(
          'is-changing'
        );


      window.setTimeout(
        () => {
          this.lightboxImage.src =
            sourceImage.currentSrc ||
            sourceImage.src;


          this.lightboxImage.alt =
            sourceImage.alt ||
            '';


          this.lightboxImage
            .classList
            .remove(
              'is-changing'
            );
        },
        100
      );


      if (
        this.lightboxCounter
      ) {
        this.lightboxCounter.textContent =
          `${index + 1} / ${this.media.length}`;
      }


      this.lightboxThumbs.forEach(
        (thumb, thumbIndex) => {
          if (!thumb) {
            return;
          }

          thumb.classList.toggle(
            'is-active',
            thumbIndex === index
          );
        }
      );


      const activeThumb =
        this.lightboxThumbs[index];


      if (activeThumb) {
        activeThumb.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }


    showPreviousLightbox() {
      if (!this.media.length) {
        return;
      }


      const previous =
        (
          this.lightboxIndex -
          1 +
          this.media.length
        ) %
        this.media.length;


      this.showLightboxMedia(
        previous
      );
    }


    showNextLightbox() {
      if (!this.media.length) {
        return;
      }


      const next =
        (
          this.lightboxIndex +
          1
        ) %
        this.media.length;


      this.showLightboxMedia(
        next
      );
    }


    setupLightboxSwipe() {
      if (!this.lightbox) {
        return;
      }


      const area =
        this.lightbox.querySelector(
          '.studio-product-lightbox__image-wrap'
        );


      if (!area) {
        return;
      }


      let startX = 0;
      let endX = 0;


      area.addEventListener(
        'touchstart',
        (event) => {
          startX =
            event.touches[0]
              .clientX;

          endX =
            startX;
        },
        {
          passive: true
        }
      );


      area.addEventListener(
        'touchmove',
        (event) => {
          endX =
            event.touches[0]
              .clientX;
        },
        {
          passive: true
        }
      );


      area.addEventListener(
        'touchend',
        () => {
          const distance =
            endX - startX;


          if (
            Math.abs(distance) <
            45
          ) {
            return;
          }


          if (distance < 0) {
            this.showNextLightbox();
          } else {
            this.showPreviousLightbox();
          }
        }
      );
    }    /* ==========================================================
       MOBILE ADD
       ========================================================== */

    setupMobileAdd() {
      if (
        !this.mobileAdd ||
        !this.addButton
      ) {
        return;
      }

      this.mobileAdd.addEventListener(
        'click',
        () => {
          this.addButton.click();
        }
      );
    }


    /* ==========================================================
       WISHLIST
       ========================================================== */

    setupWishlist() {}


    money(cents) {
      const amount =
        Number(cents || 0) /
        100;

      const currency =
        window.Shopify
          ?.currency
          ?.active ||
        'EUR';


      try {
        return new Intl
          .NumberFormat(
            document.documentElement.lang ||
            'nl-NL',
            {
              style: 'currency',
              currency
            }
          )
          .format(amount);

      } catch (error) {

        return `€${amount
          .toFixed(2)
          .replace('.', ',')}`;
      }
    }
  }


  /* ==========================================================
     INIT
     ========================================================== */

  const initStudioProducts =
    (root = document) => {

      root
        .querySelectorAll(
          '[data-studio-product-v2]'
        )
        .forEach(
          (section) => {

            if (
              section.dataset
                .studioPdpInitialized ===
              'true'
            ) {
              return;
            }


            section.dataset
              .studioPdpInitialized =
              'true';


            new StudioPremiumProduct(
              section
            );

          }
        );

    };


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initStudioProducts();
      }
    );

  } else {

    initStudioProducts();

  }


  document.addEventListener(
    'shopify:section:load',
    (event) => {

      initStudioProducts(
        event.target
      );

    }
  );

})();

/* ZOETHOUT TRIXIE PERSONALISATION V1 */
(() => {
  'use strict';

  const SERVICE_VARIANT_ID = 58768363061622;

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
    }[char]));
  }

  function createFontOptions(prefix) {
    const fonts = [
      ['Strak', 'Arial, sans-serif'],
      ['Klassiek', 'Georgia, serif'],
      ['Speels', '"Trebuchet MS", sans-serif'],
      ['Handgeschreven', '"Comic Sans MS", cursive']
    ];
    return fonts.map(([name, family], index) => `
      <label class="studio-pers-choice">
        <input type="radio" name="properties[Lettertype]" value="${name}" ${index === 0 ? 'checked' : ''}>
        <span style="font-family:${family}">${name}</span>
      </label>
    `).join('');
  }

  function styles() {
    if (document.getElementById('studio-trixie-personalisation-style')) return;
    const style = document.createElement('style');
    style.id = 'studio-trixie-personalisation-style';
    style.textContent = `
      .studio-personalisation{margin:22px 0;padding:20px;border:1px solid rgba(17,17,17,.12);border-radius:14px;background:#fff}
      .studio-personalisation__title{font-weight:700;font-size:18px;margin:0 0 6px}
      .studio-personalisation__sub{font-size:14px;opacity:.72;margin:0 0 16px}
      .studio-personalisation label.studio-pers-label{display:block;font-weight:600;font-size:14px;margin:14px 0 7px}
      .studio-personalisation input[type="text"]{width:100%;min-height:46px;border:1px solid rgba(17,17,17,.2);border-radius:9px;padding:10px 12px;font:inherit}
      .studio-pers-count{font-size:12px;opacity:.65;text-align:right;margin-top:4px}
      .studio-pers-choices{display:flex;flex-wrap:wrap;gap:8px}
      .studio-pers-choice{position:relative;cursor:pointer}
      .studio-pers-choice input{position:absolute;opacity:0;pointer-events:none}
      .studio-pers-choice span{display:block;border:1px solid rgba(17,17,17,.18);border-radius:9px;padding:9px 12px;background:#fff}
      .studio-pers-choice input:checked + span{border-color:#111;box-shadow:0 0 0 1px #111}
      .studio-pers-toggle{display:flex;align-items:center;gap:10px;font-weight:700;cursor:pointer}
      .studio-pers-toggle input{width:19px;height:19px}
      .studio-pers-details[hidden]{display:none!important}
      .studio-pers-preview{margin-top:12px;padding:14px;border-radius:10px;background:rgba(0,0,0,.035);text-align:center;font-size:22px;min-height:54px}
    `;
    document.head.appendChild(style);
  }

  function setupBottle(form) {
    if (form.dataset.trixiePersonalisation === 'bottle') return;
    form.dataset.trixiePersonalisation = 'bottle';
    const anchor=form.querySelector('.studio-pdp-quantity-label') || form.firstElementChild;
    const box=document.createElement('div');
    box.className='studio-personalisation';
    box.innerHTML=`
      <p class="studio-personalisation__title">Personaliseer je drinkfles</p>
      <p class="studio-personalisation__sub">Vul een naam in van maximaal 8 tekens en kies het lettertype.</p>
      <label class="studio-pers-label" for="trixie-bottle-name">Naam</label>
      <input id="trixie-bottle-name" type="text" name="properties[Naam]" maxlength="8" required autocomplete="off" placeholder="Bijv. Sophie">
      <div class="studio-pers-count"><span>0</span>/8</div>
      <label class="studio-pers-label">Lettertype</label>
      <div class="studio-pers-choices">${createFontOptions('bottle')}</div>
      <div class="studio-pers-preview" aria-live="polite">Jouw naam</div>
    `;
    form.insertBefore(box,anchor);
    const input=box.querySelector('#trixie-bottle-name');
    const count=box.querySelector('.studio-pers-count span');
    const preview=box.querySelector('.studio-pers-preview');
    const update=()=>{
      count.textContent=input.value.length;
      preview.textContent=input.value || 'Jouw naam';
      const chosen=box.querySelector('input[name="properties[Lettertype]"]:checked')?.value;
      const fam={Strak:'Arial, sans-serif',Klassiek:'Georgia, serif',Speels:'"Trebuchet MS", sans-serif',Handgeschreven:'"Comic Sans MS", cursive'}[chosen];
      if(fam) preview.style.fontFamily=fam;
    };
    input.addEventListener('input',update);
    box.querySelectorAll('input[name="properties[Lettertype]"]').forEach(x=>x.addEventListener('change',update));
    update();
  }

  function setupBackpack(form) {
    if (form.dataset.trixiePersonalisation === 'backpack') return;
    form.dataset.trixiePersonalisation='backpack';
    const anchor=form.querySelector('.studio-pdp-quantity-label') || form.firstElementChild;
    const box=document.createElement('div');
    box.className='studio-personalisation';
    box.innerHTML=`
      <label class="studio-pers-toggle">
        <input type="checkbox" data-pers-toggle>
        <span>Personaliseren met naam <strong>+ €6,95</strong></span>
      </label>
      <div class="studio-pers-details" data-pers-details hidden>
        <p class="studio-personalisation__sub" style="margin-top:14px">Naam maximaal 7 tekens. Kies daarna lettertype en garenkleur.</p>
        <label class="studio-pers-label" for="trixie-backpack-name">Naam</label>
        <input id="trixie-backpack-name" type="text" maxlength="7" autocomplete="off" placeholder="Bijv. Sophie" disabled>
        <div class="studio-pers-count"><span>0</span>/7</div>
        <label class="studio-pers-label">Lettertype</label>
        <div class="studio-pers-choices" data-fonts>${createFontOptions('bag').replaceAll('name="properties[Lettertype]"','name="trixie_backpack_font"')}</div>
        <label class="studio-pers-label">Garenkleur</label>
        <div class="studio-pers-choices" data-colors>
          ${['Wit','Zwart','Goud','Zilver','Roze','Lichtroze','Rood','Blauw','Lichtblauw','Groen','Mint','Paars','Oranje'].map((c,i)=>`<label class="studio-pers-choice"><input type="radio" name="trixie_backpack_color" value="${c}" ${i===0?'checked':''}><span>${c}</span></label>`).join('')}
        </div>
        <div class="studio-pers-preview" aria-live="polite">Jouw naam</div>
      </div>
    `;
    form.insertBefore(box,anchor);
    const toggle=box.querySelector('[data-pers-toggle]');
    const details=box.querySelector('[data-pers-details]');
    const input=box.querySelector('#trixie-backpack-name');
    const count=box.querySelector('.studio-pers-count span');
    const preview=box.querySelector('.studio-pers-preview');
    const update=()=>{
      count.textContent=input.value.length;
      preview.textContent=input.value || 'Jouw naam';
      const chosen=box.querySelector('input[name="trixie_backpack_font"]:checked')?.value;
      const fam={Strak:'Arial, sans-serif',Klassiek:'Georgia, serif',Speels:'"Trebuchet MS", sans-serif',Handgeschreven:'"Comic Sans MS", cursive'}[chosen];
      if(fam) preview.style.fontFamily=fam;
    };
    toggle.addEventListener('change',()=>{
      details.hidden=!toggle.checked;
      input.disabled=!toggle.checked;
      input.required=toggle.checked;
      if(toggle.checked) setTimeout(()=>input.focus(),50);
    });
    input.addEventListener('input',update);
    box.querySelectorAll('input[name="trixie_backpack_font"]').forEach(x=>x.addEventListener('change',update));
    update();

    form.addEventListener('submit',async(event)=>{
      if(!toggle.checked) return;
      event.preventDefault();
      if(!form.reportValidity()) return;
      const variantId=Number(form.querySelector('[name="id"]')?.value);
      const quantity=Math.max(1,Number(form.querySelector('[name="quantity"]')?.value||1));
      const name=input.value.trim();
      const font=box.querySelector('input[name="trixie_backpack_font"]:checked')?.value || 'Strak';
      const color=box.querySelector('input[name="trixie_backpack_color"]:checked')?.value || 'Wit';
      const addButton=form.querySelector('[data-add-to-cart]');
      if(addButton){addButton.disabled=true;addButton.setAttribute('aria-busy','true');}
      try {
        const root=(window.Shopify?.routes?.root||'/');
        const response=await fetch(root+'cart/add.js',{
          method:'POST',
          headers:{'Content-Type':'application/json','Accept':'application/json'},
          body:JSON.stringify({items:[
            {id:variantId,quantity,properties:{Naam:name,Lettertype:font,Garenkleur:color,Personalisatie:'Ja'}},
            {id:SERVICE_VARIANT_ID,quantity,properties:{Voor:name,Product:'Trixie rugzak'}}
          ]})
        });
        if(!response.ok) throw new Error(await response.text());
        window.location.href=root+'cart';
      } catch(error) {
        console.error('Trixie personalisatie toevoegen mislukt',error);
        alert('Personalisatie kon niet worden toegevoegd. Probeer het opnieuw.');
        if(addButton){addButton.disabled=false;addButton.removeAttribute('aria-busy');}
      }
    });
  }

  function init() {
    styles();
    const path=window.location.pathname.toLowerCase();
    if(!path.includes('/products/trixie-') || !path.includes('-met-naam')) return;
    const form=document.querySelector('[data-studio-product-v2] form.studio-pdp-form');
    if(!form) return;
    if(path.includes('trixie-drinkfles-')) setupBottle(form);
    if(path.includes('trixie-rugzak-')) setupBackpack(form);
  }

  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',init);
  else init();
  document.addEventListener('shopify:section:load',()=>setTimeout(init,50));
})();
